import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ImagePlus, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { GalleryUploader } from "@/components/GalleryUploader";

const SECTORS = [
  "Agriculture", "Technology", "Retail & Trade", "Food & Hospitality",
  "Health & Wellness", "Education", "Finance & Legal",
  "Construction & Real Estate", "Creative & Media", "Fashion & Beauty",
  "Manufacturing", "Transport & Logistics", "Professional Services", "Other",
];

const inputCls =
  "w-full rounded-xl border border-stone_warm bg-white px-4 py-3 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55";

export default function OwnerEdit() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("loading");
  const [logo, setLogo] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [stats, setStats] = useState({ views: 0, referral_count: 0 });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    api.get(`/owner/${token}`)
      .then((res) => {
        const b = res.data;
        setForm({
          owner_name: b.owner_name, business_name: b.business_name, sector: b.sector,
          phone: b.phone, email: b.email, description: b.description, location: b.location,
          website: b.website || "", social: b.social || "", offerings: b.offerings, needs: b.needs || "",
        });
        if (b.logo_path) {
          setLogo({ path: b.logo_path, preview: `${process.env.REACT_APP_BACKEND_URL}/api/files/${b.logo_path}` });
        }
        setGallery((b.gallery || []).map((g) => {
          const path = typeof g === "string" ? g : g.path;
          return {
            path,
            preview: `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`,
            caption: typeof g === "string" ? "" : g.caption || "",
          };
        }));
        setStats({ views: b.views || 0, referral_count: b.referral_count || 0 });
        setStatus("ready");
      })
      .catch(() => setStatus("missing"));
  }, [token]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads", fd);
      setLogo({ path: res.data.path, preview: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not upload the image."));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/owner/${token}`, { ...form, logo_path: logo?.path || "", gallery: gallery.map((g) => ({ path: g.path, caption: g.caption || "" })) });
      toast.success(`${res.data.business_name} updated.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not save changes."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="owner-edit-page">
      <header className="border-b border-stone_warm bg-ivory/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" className="flex items-center gap-2.5" data-testid="owner-logo-link">
            <span className="inline-block h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
            <span className="font-serif text-xl font-semibold tracking-tight text-charcoal">Joseph Guild</span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/45">Owner edit link</span>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 pb-28 pt-16 lg:pt-24">
        {status === "loading" && (
          <p className="py-24 text-center font-mono text-xs uppercase tracking-[0.24em] text-charcoal/40" data-testid="owner-edit-loading">
            Opening your listing…
          </p>
        )}

        {status === "missing" && (
          <div className="border border-dashed border-stone_warm bg-white/60 py-24 text-center" data-testid="owner-edit-missing">
            <p className="font-serif text-3xl font-light italic text-charcoal/55">This edit link is not valid.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-gold-dark underline-offset-4 hover:underline" data-testid="owner-missing-back">
              <ArrowLeft size={12} /> Back to the Guild
            </Link>
          </div>
        )}

        {status === "ready" && form && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.3em] text-gold-dark">Your listing</p>
            <h1 className="font-serif text-4xl font-light tracking-tighter text-charcoal sm:text-5xl">{form.business_name}</h1>
            <p className="mt-4 text-sm leading-relaxed text-charcoal/60">
              Update your details below — changes appear in the directory immediately. Anyone with this
              link can edit your listing, so keep it private.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4" data-testid="owner-analytics">
              <div className="border border-stone_warm bg-white p-5">
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/45">Profile views</p>
                <p className="mt-2 font-serif text-3xl font-light text-charcoal" data-testid="owner-views-count">{stats.views}</p>
              </div>
              <div className="border border-stone_warm bg-white p-5">
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/45">Referral requests</p>
                <p className="mt-2 font-serif text-3xl font-light text-charcoal" data-testid="owner-referrals-count">{stats.referral_count}</p>
              </div>
            </div>

            <form onSubmit={handleSave} className="mt-10 space-y-6 rounded-2xl border border-stone_warm bg-white p-8 shadow-[0_8px_30px_rgba(26,26,26,0.04)] sm:p-10" data-testid="owner-edit-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="oe-owner" className={labelCls}>Owner name</label>
                  <input id="oe-owner" required value={form.owner_name} onChange={set("owner_name")} className={inputCls} data-testid="owner-owner-name-input" />
                </div>
                <div>
                  <label htmlFor="oe-business" className={labelCls}>Business name</label>
                  <input id="oe-business" required value={form.business_name} onChange={set("business_name")} className={inputCls} data-testid="owner-business-name-input" />
                </div>
                <div>
                  <label htmlFor="oe-sector" className={labelCls}>Sector</label>
                  <select id="oe-sector" required value={form.sector} onChange={set("sector")} className={inputCls} data-testid="owner-sector-select">
                    {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="oe-phone" className={labelCls}>Phone</label>
                  <input id="oe-phone" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="owner-phone-input" />
                </div>
                <div>
                  <label htmlFor="oe-email" className={labelCls}>Email</label>
                  <input id="oe-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="owner-email-input" />
                </div>
                <div>
                  <label htmlFor="oe-location" className={labelCls}>Location</label>
                  <input id="oe-location" required value={form.location} onChange={set("location")} className={inputCls} data-testid="owner-location-input" />
                </div>
                <div>
                  <label htmlFor="oe-website" className={labelCls}>Website</label>
                  <input id="oe-website" value={form.website} onChange={set("website")} className={inputCls} data-testid="owner-website-input" />
                </div>
                <div>
                  <label htmlFor="oe-social" className={labelCls}>Social handle</label>
                  <input id="oe-social" value={form.social} onChange={set("social")} className={inputCls} data-testid="owner-social-input" />
                </div>
              </div>
              <div>
                <label htmlFor="oe-description" className={labelCls}>About the business</label>
                <textarea id="oe-description" required rows={3} value={form.description} onChange={set("description")} className={inputCls} data-testid="owner-description-input" />
              </div>
              <div>
                <label htmlFor="oe-offerings" className={labelCls}>Products & services</label>
                <textarea id="oe-offerings" required rows={3} value={form.offerings} onChange={set("offerings")} className={inputCls} data-testid="owner-offerings-input" />
              </div>
              <div>
                <label htmlFor="oe-needs" className={labelCls}>Needs help with</label>
                <textarea id="oe-needs" rows={2} value={form.needs} onChange={set("needs")} className={inputCls} data-testid="owner-needs-input" />
              </div>

              <div>
                <span className={labelCls}>Logo or photo</span>
                <div className="flex items-center gap-5">
                  {logo ? (
                    <img src={logo.preview} alt="Logo preview" className="h-16 w-16 rounded-full border border-stone_warm object-cover" data-testid="owner-logo-preview" />
                  ) : (
                    <span className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-stone_warm bg-[#F3F0EB] text-charcoal/35">
                      <ImagePlus size={18} />
                    </span>
                  )}
                  <label className="cursor-pointer rounded-full border border-charcoal/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark" data-testid="owner-logo-label">
                    {uploading ? "Uploading…" : logo ? "Replace image" : "Choose image"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogo} disabled={uploading} data-testid="owner-logo-input" />
                  </label>
                </div>
              </div>

              <GalleryUploader gallery={gallery} onChange={setGallery} testIdPrefix="owner-gallery" />

              <button
                type="submit"
                disabled={saving || uploading}
                className="inline-flex items-center gap-2 rounded-full bg-charcoal px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold disabled:opacity-60"
                data-testid="owner-save-button"
              >                {saving && <Loader2 size={13} className="animate-spin" />}
                {saving ? "Saving…" : "Save changes"}
              </button>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
}
